import ks from "@shared/kalmstromShared4";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBListManager from "./DBListManager";
import DBFieldManager from "./DBFieldManager";
import DBViewManager from "./DBViewManager";
import DBListSettings from "./DBListSettings";
import DBDataGrid from "./DBDataGrid";
import DBCSVImport from "./DBCSVImport";
import DBCanvas from "./DBCanvas";
import DBImportExport from "./DBImportExport";
import DBTemplateManager from "./DBTemplateManager";
import DBOutputSidebar from "./DBOutputSidebar";
import DBStringUtils from "./DBStringUtils";
import DBGeneratorJSON from "./generators/DBGeneratorJSON";
import DBGeneratorPowerShell from "./generators/DBGeneratorPowerShell";
import DBGeneratorSiteScript from "./generators/DBGeneratorSiteScript";
import DBGeneratorPnPXml from "./generators/DBGeneratorPnPXml";
import DBGeneratorPowerAutomate from "./generators/DBGeneratorPowerAutomate";
import DBFieldEditor from "./DBFieldEditor";

export default class DBEventRouter {

    public static SetupAll = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBEventRouter.SetupDataActionDelegation();
            DBEventRouter.SetupKeyboardActionDelegation();
            DBEventRouter.SetupKeyboardShortcuts();
            DBEventRouter.SetupAppCodeInput();
            DBEventRouter.SetupFieldNameInput();
            DBEventRouter.SetupModalOverlayClose();
            DBEventRouter.SetupSearchInput();
            DBEventRouter.SetupCSVFileInput();
            DBEventRouter.SetupCanvasDragEvents();
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupAll");
        }
    };

    public static SetupCSVFileInput = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const input: HTMLInputElement | null = document.getElementById("csvFileInput") as HTMLInputElement;
            if (input === null) { return; }
            const HandleChange = (e: Event): void => {
                DBCSVImport.HandleCSVFile(e);
            };
            input.addEventListener("change", HandleChange);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupCSVFileInput");
        }
    };

    public static SetupCanvasDragEvents = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const HandleMove = (e: MouseEvent): void => { DBCanvas.HandleMouseMove(e); };
            const HandleUp = (): void => { DBCanvas.HandleMouseUp(); };
            document.addEventListener("mousemove", HandleMove as EventListener);
            document.addEventListener("mouseup", HandleUp);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupCanvasDragEvents");
        }
    };

    public static SetupDataActionDelegation = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const HandleClick = (e: Event): void => {
                const target: HTMLElement = e.target as HTMLElement;
                const button: HTMLElement | null = target.closest("[data-action]") as HTMLElement;
                if (button === null) { return; }
                const action: string = button.dataset.action || "";
                const contextMenu: HTMLElement | null = button.closest("#contextMenu") as HTMLElement | null;
                const listId: string = button.dataset.listId || contextMenu?.dataset.listId || "";
                const fieldName: string = button.dataset.fieldName || "";
                e.stopPropagation();
                DBEventRouter.HandleDataAction(action, listId, fieldName, button, e);
            };
            document.addEventListener("click", HandleClick);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupDataActionDelegation");
        }
    };

    public static SetupKeyboardActionDelegation = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const HandleKeyDown = (e: KeyboardEvent): void => {
                if (e.key !== "Enter" && e.key !== " ") { return; }
                const target: HTMLElement = e.target as HTMLElement;
                const trigger: HTMLElement | null = target.closest("[data-action][role=\"button\"]") as HTMLElement;
                if (trigger === null) { return; }
                const action: string = trigger.dataset.action || "";
                const listId: string = trigger.dataset.listId || "";
                const fieldName: string = trigger.dataset.fieldName || "";
                e.preventDefault();
                e.stopPropagation();
                DBEventRouter.HandleDataAction(action, listId, fieldName, trigger, e);
            };
            document.addEventListener("keydown", HandleKeyDown as EventListener);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupKeyboardActionDelegation");
        }
    };

    public static HandleDataAction = (action: string, listId: string, fieldName: string, button: HTMLElement, e: Event): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const handled: boolean = DBEventRouter.HandleToolbarAction(action);
            if (handled === true) { return; }
            const handledList: boolean = DBEventRouter.HandleListAction(action, listId, fieldName, e);
            if (handledList === true) { return; }
            const handledView: boolean = DBEventRouter.HandleViewAction(action, listId, button, e);
            if (handledView === true) { return; }
            DBEventRouter.HandleModalAction(action, listId, button);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.HandleDataAction");
        }
    };

    public static HandleToolbarAction = (action: string): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            switch (action) {
                case "add-list": DBListManager.AddList(); return true;
                case "load-example": DBTemplateManager.LoadExampleData(); return true;
                case "show-json": DBGeneratorJSON.ShowJSON(); return true;
                case "show-powershell": DBGeneratorPowerShell.ShowPowerShell(); return true;
                case "show-sitescript": DBGeneratorSiteScript.ShowSiteScript(); return true;
                case "show-pnp-xml": DBGeneratorPnPXml.ShowPnPXml(); return true;
                case "show-power-automate": DBGeneratorPowerAutomate.ShowPowerAutomate(); return true;
                case "export-power-automate": DBGeneratorPowerAutomate.ExportFlowPackage(); return true;
                case "export-flow-zip": DBGeneratorPowerAutomate.ExportFlowPackage(); return true;
                case "clear-all": DBTemplateManager.ClearAll(); return true;
                case "import-schema": DBImportExport.ImportSchema(); return true;
                case "import-rest-api": DBImportExport.OpenRestApiModal(); return true;
                case "undo": DBState.Undo(); return true;
                case "redo": DBState.Redo(); return true;
                case "show-keyboard-shortcuts": DBModalManager.ShowKeyboardShortcuts(); return true;
                case "save-template": DBTemplateManager.OpenSaveTemplateModal(); return true;
                case "load-template": DBTemplateManager.OpenTemplateModal(); return true;
                case "confirm-save-template": DBTemplateManager.SaveAsTemplate(); return true;
                case "auto-arrange": DBListManager.AutoArrangeLists(); return true;
                case "zoom-in": DBListManager.ZoomCanvas(1.2); return true;
                case "zoom-out": DBListManager.ZoomCanvas(0.8); return true;
                case "zoom-reset": DBListManager.ResetZoom(); return true;
                default: return false;
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.HandleToolbarAction");
            return false;
        }
    };

    public static HandleListAction = (action: string, listId: string, fieldName: string, e: Event): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            switch (action) {
                case "open-data-modal":
                case "open-data-modal-ctx": DBDataGrid.OpenDataModal(listId); return true;
                case "toggle-collapse":
                case "toggle-collapse-ctx": DBListManager.ToggleCollapse(listId); return true;
                case "toggle-content-type": DBListManager.ToggleContentType(listId); return true;
                case "duplicate-list":
                case "duplicate-list-ctx": DBListManager.DuplicateList(listId); return true;
                case "delete-list":
                case "delete-list-ctx": DBListManager.DeleteList(listId); return true;
                case "open-list-settings":
                case "list-settings-ctx": DBListSettings.OpenListSettings(listId); return true;
                case "open-field-editor": DBFieldEditor.OpenFieldEditor(listId); return true;
                case "save-list-settings": DBListSettings.SaveListSettings(); return true;
                case "save-field-settings": DBListSettings.SaveFieldSettings(); return true;
                case "open-field-settings":
                    e.stopPropagation();
                    DBListSettings.OpenFieldSettings(listId, fieldName);
                    return true;
                case "delete-field":
                    e.stopPropagation();
                    DBFieldManager.DeleteField(listId, fieldName);
                    return true;
                default: return false;
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.HandleListAction");
            return false;
        }
    };

    public static HandleViewAction = (action: string, listId: string, button: HTMLElement, e: Event): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            switch (action) {
                case "open-view-editor": DBViewManager.OpenViewEditor(listId); return true;
                case "manage-views":
                case "manage-views-ctx": DBViewManager.OpenManageViewsModal(listId); return true;
                case "add-view": DBViewManager.OpenViewEditor(listId); return true;
                case "edit-view":
                    e.stopPropagation();
                    DBViewManager.EditView(listId, button.dataset.viewId || "");
                    return true;
                case "delete-view":
                    e.stopPropagation();
                    DBViewManager.DeleteView(listId, button.dataset.viewId || "");
                    return true;
                case "save-view": DBViewManager.SaveView(); return true;
                case "add-view-aggregation": DBViewManager.AddViewAggregation(); return true;
                case "remove-view-aggregation":
                    DBViewManager.RemoveViewAggregation(parseInt(button.dataset.aggIndex || "0"));
                    return true;
                case "move-view-field-up":
                    e.stopPropagation();
                    DBViewManager.MoveViewFieldUp(parseInt(button.dataset.index || "0"));
                    return true;
                case "move-view-field-down":
                    e.stopPropagation();
                    DBViewManager.MoveViewFieldDown(parseInt(button.dataset.index || "0"));
                    return true;
                default: return false;
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.HandleViewAction");
            return false;
        }
    };

    public static HandleModalAction = (action: string, listId: string, button: HTMLElement): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            switch (action) {
                case "close-modal":
                    DBEventRouter.HandleCloseModal(button);
                    return true;
                case "save-field": DBFieldManager.SaveField(); return true;
                case "add-option": DBFieldManager.AddOption(); return true;
                case "process-import": DBImportExport.ProcessImport(); return true;
                case "import-rest-api-data": DBImportExport.ImportFromRestApi(); return true;
                case "copy-rest-api-url": DBImportExport.CopyRestApiUrl(); return true;
                case "copy-output": DBOutputSidebar.CopyOutput(); return true;
                case "export-output": DBOutputSidebar.ExportOutput(); return true;
                case "close-output-sidebar": DBOutputSidebar.CloseOutputSidebar(); return true;
                case "download-all-outputs": DBOutputSidebar.DownloadAllOutputs(); return true;
                case "toggle-output-file": DBOutputSidebar.ToggleOutputFile(parseInt(button.dataset.fileIndex || "0")); return true;
                case "download-single-output": DBOutputSidebar.DownloadSingleOutput(parseInt(button.dataset.fileIndex || "0")); return true;
                case "copy-json": DBGeneratorJSON.CopyJSON(); return true;
                case "create-list":
                    DBListManager.CreateListOrLibrary(button.dataset.listType || "list", button.dataset.libraryType || "Generic List");
                    return true;
                case "add-data-row": DBDataGrid.AddDataRow(); return true;
                case "import-csv": DBCSVImport.ImportCSV(); return true;
                case "paste-data": DBDataGrid.PasteData(); return true;
                case "open-rest-api-data-modal": DBImportExport.OpenRestApiModal(); return true;
                case "clear-all-data": DBDataGrid.ClearAllData(); return true;
                case "save-data": DBDataGrid.SaveData(); return true;
                case "save-field-editor": DBFieldEditor.SaveFieldEditor(); return true;
                case "confirm-csv-import": DBCSVImport.ConfirmCSVImport(); return true;
                case "load-built-in-template":
                    DBTemplateManager.LoadTemplate("built-in", parseInt(button.dataset.templateIndex || "0"));
                    return true;
                case "load-user-template":
                    DBTemplateManager.LoadTemplate("user", parseInt(button.dataset.templateIndex || "0"));
                    return true;
                case "delete-user-template":
                    DBTemplateManager.DeleteTemplate(parseInt(button.dataset.templateIndex || "0"));
                    return true;
                case "export-single-list-json":
                    DBGeneratorJSON.ExportSingleListJSON(listId);
                    return true;
                case "process-rest-api-data":
                    DBImportExport.ProcessRestApiData();
                    return true;
                default: return false;
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.HandleModalAction");
            return false;
        }
    };

    public static HandleCloseModal = (button: HTMLElement): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const modalId: string = button.dataset.modalId || "";
            if (modalId === "dataModal") {
                DBDataGrid.CloseDataModal();
            } else {
                DBModalManager.CloseModal(modalId);
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.HandleCloseModal");
        }
    };

    public static SetupKeyboardShortcuts = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const HandleKeyDown = (e: KeyboardEvent): void => {
                DBEventRouter.ProcessKeyboardShortcut(e);
            };
            document.addEventListener("keydown", HandleKeyDown);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupKeyboardShortcuts");
        }
    };

    public static ProcessKeyboardShortcut = (e: KeyboardEvent): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
                e.preventDefault();
                DBState.Undo();
            } else if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === "z" || e.key === "y")) {
                e.preventDefault();
                DBState.Redo();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                e.preventDefault();
                DBState.SaveToStorage();
                DBModalManager.ShowToast("Saved manually", "success");
            } else if ((e.ctrlKey || e.metaKey) && e.key === "j") {
                e.preventDefault();
                DBGeneratorJSON.ShowJSON();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "n") {
                e.preventDefault();
                DBListManager.AddList();
            } else if (e.shiftKey && e.key === "?") {
                e.preventDefault();
                DBModalManager.ShowKeyboardShortcuts();
            } else if (e.key === "Escape") {
                DBModalManager.CloseAllModals();
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.ProcessKeyboardShortcut");
        }
    };

    public static SetupAppCodeInput = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const appCodeInput: HTMLElement | null = document.getElementById("appCodeInput");
            if (appCodeInput === null) { return; }
            const HandleAppCodeInput = (e: Event): void => {
                const input: HTMLInputElement = e.target as HTMLInputElement;
                input.value = input.value.toUpperCase().slice(0, 2);
            };
            appCodeInput.addEventListener("input", HandleAppCodeInput);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupAppCodeInput");
        }
    };

    public static SetupFieldNameInput = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldDisplayName: HTMLElement | null = document.getElementById("fieldDisplayName");
            if (fieldDisplayName === null) { return; }
            const HandleFieldNameInput = (e: Event): void => {
                const input: HTMLInputElement = e.target as HTMLInputElement;
                const internalNameInput: HTMLInputElement | null = document.getElementById("fieldInternalName") as HTMLInputElement;
                if (internalNameInput === null) { return; }
                internalNameInput.value = DBStringUtils.UpdateInternalName(input.value, DBState.GetAppCode());
            };
            fieldDisplayName.addEventListener("input", HandleFieldNameInput);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupFieldNameInput");
        }
    };

    public static SetupModalOverlayClose = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const overlays: NodeListOf<Element> = document.querySelectorAll(".modal-overlay");
            const SetupOverlay = (overlay: Element): void => {
                const HandleOverlayClick = (e: Event): void => {
                    if (e.target === overlay) {
                        (overlay as HTMLElement).classList.remove("active");
                    }
                };
                overlay.addEventListener("click", HandleOverlayClick);
            };
            overlays.forEach(SetupOverlay);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupModalOverlayClose");
        }
    };

    public static SetupSearchInput = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const searchInput: HTMLElement | null = document.getElementById("listSearchInput");
            if (searchInput === null) { return; }
            const HandleSearchInput = (e: Event): void => {
                const input: HTMLInputElement = e.target as HTMLInputElement;
                const query: string = input.value.toLowerCase().trim();
                DBEventRouter.FilterListCards(query);
            };
            searchInput.addEventListener("input", HandleSearchInput);
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.SetupSearchInput");
        }
    };

    public static FilterListCards = (query: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const cards: NodeListOf<Element> = document.querySelectorAll(".list-card");
            let matchCount: number = 0;
            const ProcessCard = (card: Element): void => {
                const matched: boolean = DBEventRouter.DoesCardMatchQuery(card, query);
                const cardElement: HTMLElement = card as HTMLElement;
                if (query === "" || matched === true) {
                    cardElement.style.display = "";
                    cardElement.style.opacity = "";
                    matchCount = matchCount + 1;
                } else {
                    cardElement.style.display = "none";
                    cardElement.style.opacity = "0.3";
                }
            };
            cards.forEach(ProcessCard);
            DBEventRouter.UpdateSearchEmptyState(matchCount, query);
            DBCanvas.DrawRelationships();
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.FilterListCards");
        }
    };

    public static DoesCardMatchQuery = (card: Element, query: string): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            if (query === "") { return true; }
            const titleElement: Element | null = card.querySelector(".list-name-display");
            if (titleElement !== null) {
                const titleText: string = titleElement.textContent || "";
                if (titleText.toLowerCase().indexOf(query) !== -1) {
                    return true;
                }
            }
            const fieldItems: NodeListOf<Element> = card.querySelectorAll(".field-item");
            let fieldMatched: boolean = false;
            const CheckField = (fieldItem: Element): void => {
                const fieldText: string = fieldItem.textContent || "";
                if (fieldText.toLowerCase().indexOf(query) !== -1) {
                    fieldMatched = true;
                }
            };
            fieldItems.forEach(CheckField);
            return fieldMatched;
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.DoesCardMatchQuery");
            return false;
        }
    };

    public static UpdateSearchEmptyState = (matchCount: number, query: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const existingEmpty: HTMLElement | null = document.getElementById("searchEmptyState");
            if (matchCount === 0 && query !== "") {
                if (existingEmpty !== null) { return; }
                const emptyDiv: HTMLDivElement = document.createElement("div");
                emptyDiv.id = "searchEmptyState";
                emptyDiv.className = "search-empty-state";
                emptyDiv.textContent = "No Results Found";
                const canvas: HTMLElement | null = document.getElementById("canvas");
                if (canvas !== null) {
                    canvas.appendChild(emptyDiv);
                }
            } else {
                if (existingEmpty !== null) {
                    const parent: HTMLElement | null = existingEmpty.parentElement;
                    if (parent !== null) {
                        parent.removeChild(existingEmpty);
                    }
                }
            }
        } catch (e: unknown) {
            ks.kGlobalErrorHandler(e, "DBEventRouter.UpdateSearchEmptyState");
        }
    };
}
